import {
  StoredUser,
  UserStatus,
  LastWatched,
  LastWatchedResponse,
  EpisodeProgressResponse,
} from '@shared/types';
// import { constructUrlWithParams } from './index.ts';
import {
  AutocompleteItem,
  RequestCollectionWordDeleteParams,
  RequestCollectionWordsParams,
  RequestWordsParams,
  RequestYoutubeWordsParams
} from '@shared/types';
import { CollectionWordsResponse, PhrasesResponse, SaveWordRequest, SaveWordResponse } from '@shared/types/words';

const getOpts: RequestInit = {
  method: 'GET',
  headers: {
    'Accept': 'application/json'
  }
};

const getAuthOpts: RequestInit = {
  ...getOpts,
  cache: "no-cache",
  credentials: import.meta.env.PROD ? 'same-origin' : 'include'
}

const postOpts: RequestInit = {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
    'Accept': 'application/json',
  },
};

const postAuthOpts: RequestInit = {
  ...postOpts,
  credentials: import.meta.env.PROD ? 'same-origin' : 'include'
};

export async function requestAuthCheck(): Promise<StoredUser | null> {
  const checkAuthEndpointUrl = `${import.meta.env.VITE_API_URL}/auth/check`;
  try {
    const resp = await fetch(checkAuthEndpointUrl, {
      ...getAuthOpts
    });
    if (resp.ok) {
      const user: StoredUser = await resp.json();
      // if cookies have valid token it returns StoredUser object, otherwise null
      return user;
    }
  } catch (err) {
    console.error(err);
  }

  return null;
}

export async function requestAuthTelegram(initData: string): Promise<StoredUser | null> {
  try {
    const telegramAuthEndpointUrl = `${import.meta.env.VITE_API_URL}/auth/telegram`;
    const resp = await fetch(telegramAuthEndpointUrl, {
      ...postAuthOpts,
      body: JSON.stringify({ initData })
    });
    if (resp.ok) {
      const user: StoredUser = await resp.json();
      return user;
    }
  } catch (err) {
    console.error(err);
  }

  return null;
}

export async function requestEmailCode(email: string): Promise<boolean> {
  const sendEmailCodeUrl = `${import.meta.env.VITE_API_URL}/auth/send-email-code`;
  try {
    const resp = await fetch(sendEmailCodeUrl, {
      ...postAuthOpts,
      body: JSON.stringify({ email }),
    });
    if (resp.ok) {
      const data: { success: boolean } = await resp.json();
      if (data?.success) {
        return true;
      }
    }
  } catch (err) {
    console.error(err);
  }

  return false;
}

export async function requestSigninByEmailCode(email: string, code: string): Promise<StoredUser | null> {
  const signinByCodeUrl = `${import.meta.env.VITE_API_URL}/auth/signin-by-email-code`;
  try {
    const resp = await fetch(signinByCodeUrl, {
      ...postAuthOpts,
      body: JSON.stringify({ email, code })
    });
    if (resp.ok) {
      const user: StoredUser = await resp.json();
      if (user?.status === UserStatus.Active) {
        return user;
      } else {
        // TODO: ...?
      }
    }
  } catch (err) {
    console.error(err);
  }

  return null;
}

export async function requestLogout(): Promise<void> {
  const logoutUrl = `${import.meta.env.VITE_API_URL}/auth/logout`;
  try {
    await fetch(logoutUrl, {
      ...getAuthOpts,
      method: 'DELETE'
    });
  } catch (err) {
    console.error(err);
  }
}

export async function requestSubtitlesAutocomplete(pageId: string, value: string): Promise<AutocompleteItem[]> {
  const autocompleteEndpointUrl = `${import.meta.env.VITE_API_URL}/textelligence/autocomplete/${pageId}/${encodeURIComponent(value)}`;
  try {
    const resp = await fetch(autocompleteEndpointUrl, {
      ...getAuthOpts,
    });
    if (resp.ok) {
      const autocompleteData: AutocompleteItem[] = await resp.json();
      return autocompleteData;
    }
  } catch (err) {
    console.error(err);
  }

  return [];
}

export async function requestSubtitlesWords(params: RequestWordsParams): Promise<PhrasesResponse> {
  const endpointUrl = `${import.meta.env.VITE_API_URL}/textelligence/words`;
  const fallbackError = 'Failed to fetch or download subtitles';
  const defaultResponse = {
    words: [],
    part: params.part || 1,
    totalParts: 0,
    wordBatchId: -1,
    screenmediaId: '',
  };
  try {
    const resp = await fetch(endpointUrl, {
      ...postAuthOpts,
      method: 'POST',
      body: JSON.stringify(params)
    });

    if (resp.status === 202) {
      return {
        ...defaultResponse,
        error: 'processing',
      };
    }

    let data: Partial<PhrasesResponse> = {};
    try {
      data = await resp.json();
    } catch {
      data = {};
    }

    if (!resp.ok) {
      return {
        ...defaultResponse,
        error: typeof data.error === 'string' ? data.error : fallbackError,
      };
    }

    if (data.words) {
      return {
        words: data.words,
        part: data.part || (params.part || 1),
        totalParts: data.totalParts || 0,
        wordBatchId: data.wordBatchId || -1,
        screenmediaId: data.screenmediaId || '',
        nextPart: data.nextPart,
        previewPending: data.previewPending,
        previewTargetSize: data.previewTargetSize,
        previewRemainderPart: data.previewRemainderPart
      };
    }
  } catch (err) {
    console.error(err);
  }

  return { ...defaultResponse, error: fallbackError };
}

export async function requestYoutubeWords(params: RequestYoutubeWordsParams): Promise<PhrasesResponse> {
  const endpointUrl = `${import.meta.env.VITE_API_URL}/textelligence/words/youtube`;
  const fallbackError = 'Failed to fetch or download YouTube subtitles';
  const defaultResponse = {
    words: [],
    part: params.part || 1,
    totalParts: 0,
    wordBatchId: -1,
    screenmediaId: '',
  };
  try {
    const resp = await fetch(endpointUrl, {
      ...postAuthOpts,
      method: 'POST',
      body: JSON.stringify(params)
    });

    if (resp.status === 202) {
      return {
        ...defaultResponse,
        error: 'processing',
      };
    }

    let data: Partial<PhrasesResponse> = {};
    try {
      data = await resp.json();
    } catch {
      data = {};
    }

    if (!resp.ok) {
      return {
        ...defaultResponse,
        error: typeof data.error === 'string' ? data.error : fallbackError,
      };
    }

    if (data.words) {
      return {
        words: data.words,
        part: data.part || (params.part || 1),
        totalParts: data.totalParts || 0,
        wordBatchId: data.wordBatchId || -1,
        screenmediaId: data.screenmediaId || '',
        mediaTitle: data.mediaTitle,
        mediaPoster: data.mediaPoster,
        nextPart: data.nextPart,
        previewPending: data.previewPending,
        previewTargetSize: data.previewTargetSize,
        previewRemainderPart: data.previewRemainderPart
      };
    }
  } catch (err) {
    console.error(err);
  }

  return { ...defaultResponse, error: fallbackError };
}

export async function requestSaveWord(params: SaveWordRequest): Promise<SaveWordResponse | null> {
  const endpointUrl = `${import.meta.env.VITE_API_URL}/user/collections/save-word`;
  try {
    const resp = await fetch(endpointUrl, {
      ...postAuthOpts,
      method: 'POST',
      body: JSON.stringify(params)
    });
    if (resp.ok) {
      const data: SaveWordResponse = await resp.json();
      return data;
    }
  } catch (err) {
    console.error(err);
  }
  return null;
}

export async function requestCollectionWords(params: RequestCollectionWordsParams): Promise<CollectionWordsResponse> {
  const endpointUrl = `${import.meta.env.VITE_API_URL}/user/collections/words`;
  const error = 'Failed to fetch your collection';
  try {
    const resp = await fetch(endpointUrl, {
      ...postAuthOpts,
      method: 'POST',
      body: JSON.stringify(params)
    });
    const { words, part, totalParts, collectionId, collectionName, collectionDescription, error }: CollectionWordsResponse = await resp.json();

    if (words) {
      return { words, part, totalParts, collectionId, collectionName, collectionDescription };
    } else if (error) {
      throw error;
    }
  } catch (err) {
    console.error(err);
  }

  return { words: [], part: params.part || 1, totalParts: 0, collectionId: '', collectionName: '', error }
}

export async function requestCollectionWordDelete(params: RequestCollectionWordDeleteParams): Promise<boolean> {
  const endpointUrl = `${import.meta.env.VITE_API_URL}/user/collections/word`;
  const error = 'Failed to delete word from collection';
  try {
    const resp = await fetch(endpointUrl, {
      ...postAuthOpts,
      method: 'DELETE',
      body: JSON.stringify(params)
    });
    if (resp.ok) {
      return true;
    } else {
      throw error;
    }
  } catch (err) {
    console.error(err);
  }

  return false;
}

export interface ProgressFilters {
  showId?: string;
  screenmediaId?: string;
  season?: number;
  episode?: number;
  includeHidden?: boolean;
}

export async function getProgress(page = 1, limit = 5, filters?: ProgressFilters): Promise<LastWatchedResponse> {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  if (filters?.showId) {
    params.append('showId', filters.showId);
  }
  if (filters?.screenmediaId) {
    params.append('screenmediaId', filters.screenmediaId);
  }
  if (typeof filters?.season === 'number') {
    params.append('season', String(filters.season));
  }
  if (typeof filters?.episode === 'number') {
    params.append('episode', String(filters.episode));
  }
  if (typeof filters?.includeHidden === 'boolean') {
    params.append('includeHidden', String(filters.includeHidden));
  }
  const queryString = params.toString();
  const url = `${import.meta.env.VITE_API_URL}/user/progress${queryString ? `?${queryString}` : ''}`;
  const resp = await fetch(url, {...getAuthOpts, method: 'GET'});
  if (resp.ok) {
    return resp.json();
  } else {
    try {
      const errorResponse: LastWatchedResponse = await resp.json();
      return Array.isArray(errorResponse?.list) ? errorResponse : {
        list: [],
        error: 'Failed to fetch progress'
      };
    } catch {
      return {list: [], error: 'Failed to fetch progress'};
    }
  }
}

export async function saveProgress(data: LastWatched): Promise<void> {
  const url = `${import.meta.env.VITE_API_URL}/user/progress`;
  const resp = await fetch(url, {
    ...postAuthOpts,
    method: 'PUT',
    body: JSON.stringify(data)
  });

  if (!resp.ok) {
    const txt = await resp.text().catch(() => '');
    throw new Error(`saveProgress failed: ${resp.status} ${txt}`);
  }
}

export async function hideProgress(progressId: string, reason = 'user_hidden'): Promise<void> {
  const url = `${import.meta.env.VITE_API_URL}/user/progress/${encodeURIComponent(progressId)}`;
  const resp = await fetch(url, {
    ...postAuthOpts,
    method: 'DELETE',
    body: JSON.stringify({ reason }),
  });

  if (!resp.ok) {
    const txt = await resp.text().catch(() => '');
    throw new Error(`hideProgress failed: ${resp.status} ${txt}`);
  }
}

export async function getEpisodeProgress(filters: {
  userId?: string;
  showId: string;
  screenmediaId?: string;
  season?: number;
  episode?: number;
}): Promise<EpisodeProgressResponse> {
  const params = new URLSearchParams({
    showId: filters.showId,
  });
  if (filters.userId) {
    params.append('userId', filters.userId);
  }
  if (filters.screenmediaId) {
    params.append('screenmediaId', filters.screenmediaId);
  }
  if (typeof filters.season === 'number') {
    params.append('season', String(filters.season));
  }
  if (typeof filters.episode === 'number') {
    params.append('episode', String(filters.episode));
  }

  const url = `${import.meta.env.VITE_API_URL}/episode-progress?${params.toString()}`;
  const resp = await fetch(url, { ...getAuthOpts, method: 'GET' });
  if (resp.ok) {
    return resp.json();
  }

  return {
    total: 0,
    remembered: 0,
    notRemembered: 0,
    completionPercent: 0,
  };
}
